import { useState } from 'react'
import AnalystCard, { ANALYSTS } from './AnalystCard'
import './CouncilBuilder.css'

const ANALYST_IDS = ['haiku', 'grok', 'sonnet', 'gemini', 'llama', 'bear', 'bull']
const JUDGES = [
  { id: 'opus', name: 'OPUS', icon: '⚖️', desc: 'The original Judge - balanced synthesis' },
  { id: 'grok', name: 'GROK', icon: '⚡', desc: 'Contrarian verdicts, chaos-friendly' },
  { id: 'gemini', name: 'GEMINI', icon: '♊', desc: 'Technical precision, data-driven' },
]
const MIN_ANALYSTS = 3
const MAX_ANALYSTS = 5

export default function CouncilBuilder({ onCouncilReady, disabled }) {
  const [selected, setSelected] = useState(new Set(['haiku', 'sonnet', 'bear'])) // Default selection
  const [judge, setJudge] = useState('opus')

  const handleToggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        if (next.size > MIN_ANALYSTS) {
          next.delete(id)
        }
      } else {
        if (next.size < MAX_ANALYSTS) {
          next.add(id)
        }
      }
      return next
    })
  }

  const handleStart = () => {
    if (selected.size >= MIN_ANALYSTS) {
      onCouncilReady?.(Array.from(selected), judge)
    }
  }

  const canRemove = selected.size > MIN_ANALYSTS
  const canAdd = selected.size < MAX_ANALYSTS

  return (
    <div className="council-builder">
      <div className="council-header">
        <h2>Build Your Council</h2>
        <p className="council-subtitle">
          Select {MIN_ANALYSTS}-{MAX_ANALYSTS} analysts to deliberate your question.
          <br />
          <span className="council-hint">Choose who delivers the final verdict.</span>
        </p>
      </div>

      <div className="council-selection">
        <div className="selection-count">
          <span className={`count ${selected.size < MIN_ANALYSTS ? 'warning' : ''}`}>
            {selected.size}/{MAX_ANALYSTS}
          </span>
          <span className="count-label">analysts selected</span>
        </div>
      </div>

      <div className="analyst-grid">
        {ANALYST_IDS.map(id => (
          <AnalystCard
            key={id}
            analystId={id}
            selected={selected.has(id)}
            onToggle={handleToggle}
            disabled={disabled || (!selected.has(id) && !canAdd) || (selected.has(id) && !canRemove)}
          />
        ))}
      </div>

      <div className="council-judge">
        <div className="judge-selector-label">Choose Your Judge</div>
        <div className="judge-selector">
          {JUDGES.map(j => (
            <button
              key={j.id}
              className={`judge-option ${judge === j.id ? 'selected' : ''}`}
              onClick={() => setJudge(j.id)}
              disabled={disabled}
            >
              <span className="judge-option-icon">{j.icon}</span>
              <div className="judge-option-info">
                <span className="judge-option-name">{j.name}</span>
                <span className="judge-option-desc">{j.desc}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="council-footer">
        <div className="council-preview">
          <span className="preview-label">Your Council:</span>
          <div className="preview-icons">
            {Array.from(selected).map(id => (
              <span key={id} className="preview-icon" style={{ '--color': ANALYSTS[id]?.color }}>
                {ANALYSTS[id]?.icon}
              </span>
            ))}
            <span className="preview-divider">→</span>
            <span className="preview-icon judge" title={`Judge: ${JUDGES.find(j => j.id === judge)?.name}`}>
              {JUDGES.find(j => j.id === judge)?.icon}
            </span>
          </div>
        </div>
        <button
          className="council-start-btn"
          onClick={handleStart}
          disabled={disabled || selected.size < MIN_ANALYSTS}
        >
          Start Deliberation
        </button>
      </div>
    </div>
  )
}
